import { useState } from "react";
import { Loader2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { api, errMsg } from "@/lib/api";
import { PEOPLE } from "@/lib/spaces";
import { useSettings } from "@/context/SettingsContext";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { BlurInput, Row, Section, TestButton, Toggle } from "@/components/settings/Controls";

export const TeamTab = () => {
  const { team, saveTeam, reloadMembers } = useSettings();
  const save = async (p, patch) => {
    try {
      await api.put(`/settings/members/${p.key}`, { name: p.name, email: p.email, ...patch });
      await reloadMembers();
      toast.success(`${patch.name || p.name} updated`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!team) return null;
  return (
    <div className="space-y-6">
      <Section title="Team members" hint="Names and the email address their reminders go to." testid="settings-team">
        {PEOPLE.map((p) => (
          <Row key={p.key} label={<span className="flex items-center gap-2"><SpaceAvatar space={p.key} /> {p.name}</span>}>
            <BlurInput value={p.name} onSave={(name) => save(p, { name })} className="w-32" testid={`member-name-${p.key}`} />
            <BlurInput type="email" value={p.email} onSave={(email) => save(p, { email })} className="w-60" testid={`member-email-${p.key}`} />
          </Row>
        ))}
      </Section>
      <Section title="Birthdays & work anniversaries" hint="Balloons on the wallboard on the day." testid="settings-dates">
        {PEOPLE.map((p) => (
          <Row key={p.key} label={p.name}>
            <span className="text-xs text-muted-foreground">Birthday</span>
            <BlurInput type="date" value={team.people[p.key]?.birthday} onSave={(birthday) => saveTeam({ people: { [p.key]: { birthday } } }, "Saved")} className="w-40 font-mono" testid={`member-birthday-${p.key}`} />
            <span className="text-xs text-muted-foreground">Started</span>
            <BlurInput type="date" value={team.people[p.key]?.start_date} onSave={(start_date) => saveTeam({ people: { [p.key]: { start_date } } }, "Saved")} className="w-40 font-mono" testid={`member-start-${p.key}`} />
          </Row>
        ))}
      </Section>
    </div>
  );
};

const FEATURES = [
  ["quick_add", "Quick add in plain English", "Type a sentence, the AI fills in the reminder."],
  ["voice", "Voice notes", "Record a note and it becomes a reminder."],
  ["duplicates", "Duplicate warnings", "Warns when a new reminder looks like an open one."],
  ["breakdown", "Break it down", "Turns a big task into a checklist."],
  ["enrich", "Enrich a ticket", "Clearer details, next step and missing info."],
  ["summary", "Summarise history", "Long tickets in three lines."],
  ["draft", "Draft a reply", "Email or team update written from the ticket."],
  ["snooze", "Smart snooze", "Suggests sensible new reminder times."],
  ["briefing", "Morning briefing", "Your day, and what to do first."],
  ["ask", "Ask your tasks", "Chat with the AI about everyone's tasks."],
  ["weekly_recap", "Weekly recap", "The team's week, written up."],
  ["compliments", "Compliments", "A nice word when someone closes a ticket."],
  ["joke", "AI joke of the day", "Off: uses the built-in joke list."],
  ["wall_overview", "Wallboard ticket overview", "A short AI summary when a reminder is opened on the TV."],
  ["search", "AI reminder search", "Find open reminders by meaning, not just exact words."],
];
const TONES = ["Very formal", "Professional", "Friendly", "Casual", "Cheeky banter"];

export const AiTab = () => {
  const { team, saveTeam } = useSettings();
  const [recap, setRecap] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!team) return null;
  const run = async (send) => {
    setBusy(true);
    try { setRecap((await api.post("/ai/weekly-recap", null, { params: { send } })).data); if (send) toast.success("Recap emailed to the team"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-6">
      <Section title="AI personality" testid="settings-ai-tone">
        <Row label="Tone of voice" hint="Used by every AI feature.">
          <div className="flex w-72 items-center gap-3">
            <Slider min={0} max={4} step={1} value={[team.ai.tone]} onValueChange={([v]) => saveTeam({ ai: { tone: v } })} data-testid="ai-tone-slider" />
            <span className="w-28 text-right text-sm font-semibold" data-testid="ai-tone-label">{TONES[team.ai.tone]}</span>
          </div>
        </Row>
      </Section>
      <Section title="AI features" hint="Switch each one on or off for the whole team." testid="settings-ai-features">
        {FEATURES.map(([k, label, hint]) => <Toggle key={k} label={label} hint={hint} checked={team.ai.enabled[k] !== false} onChange={(v) => saveTeam({ ai: { enabled: { [k]: v } } })} testid={`ai-feature-${k}`} />)}
      </Section>
      <Section title="Weekly recap" testid="settings-recap">
        <Toggle label="Email the recap every Friday at 4pm" hint="Goes to the whole team and support." checked={team.ai.weekly_recap_email} onChange={(v) => saveTeam({ ai: { weekly_recap_email: v } }, v ? "Friday recap switched on" : "Friday recap switched off")} testid="ai-recap-email" />
        <Row label="Try it now">
          <TestButton onClick={() => run(false)} testid="ai-recap-preview">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Write this week's recap</span>}</TestButton>
          {recap && <TestButton onClick={() => run(true)} testid="ai-recap-send"><span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email it</span></TestButton>}
        </Row>
        {recap && <div className="whitespace-pre-wrap rounded-xl bg-white/[0.04] p-4 text-sm" data-testid="ai-recap-text"><p className="mb-2 font-display font-semibold">{recap.title}</p>{recap.text}</div>}
      </Section>
    </div>
  );
};
